import _ from 'lodash';
import mixin from 'privates/utils/mixin';
import Element from 'ui/Element';
import DisabledMixin from './mixins/DisabledMixin';
import HiddenCollapsedMixin from './mixins/HiddenCollapsedMixin';
import ClickableMixin from './mixins/ClickableMixin';
import * as Utils from './Utils.es6';
import * as members from 'privates/privateMembers';
import { validate, validateRange } from 'privates/validations/validation';
import { generateError } from '../privates/validations/errors.es6';
import reporter from 'privates/reporter';

/**
 * @class Pagination
 * @summary An element for working with paging.
 * @mixes $w.Element
 * @mixes $w.DisabledMixin
 * @mixes $w.HiddenCollapsedMixin
 * @mixes $w.ClickableMixin
 * @mixes $w.ViewportMixin
 * @memberof $w
 * @classdesc
 *  A pagination bar provides a way for your users to control the [paging of
 *  a dataset](wix-dataset.html#data-paging). Elements that are connected to the
 *  same dataset as the pagination bar are affected when the user
 *  interacts with the pagination bar.
 *
 *  For example, suppose you have a repeater connected to a dataset and that dataset
 *  is set to have a page size of 5. When the page loads, the repeater shows
 *  the first 5 items from the dataset. If a user clicks on the next page button
 *  of a pagination bar connected to the same dataset as the repeater,
 *  the repeater then shows items 6-10 from the dataset.
 *
 *  You can also use a pagination bar without connecting it to a dataset.
 *  When doing so, you have to provide the pagination logic that runs when
 *  a user interacts with the pagination bar.
 * @tagname pagination
 * @viewername wixui.Pagination
 */
class Pagination extends Element {

    /**
     * @function onChange
     * @syntax
     * function onChange(handler: ChangeEventHandler): Pagination
     * callback EventHandler(event, selector: Function): void
     * @summary Adds an event handler that runs when the pagination bar
     *  is changed.
     * @description
     *  A pagination bar receives a `change` event when a user changes the
     *  current page by interacting with the pagination bar.
     *
     *  A `change` event is not triggered when you change a pagination bar's
     *  current page using the [`currentPage`](#currentPage) property.
     *
     *  When a pagination bar is connected to a dataset, changing the [dataset's
     *  current page](wix-dataset.html#data-paging) using dataset functions, such
     *  as [`nextPage()`](wix-dataset.html#nextPage), do not trigger `change`
     *  events on the pagination bar.
     * @snippet [Pagination-onChange.es6=Get the new current page of the pagination bar that was changed]
     * @param {$w.EventHandler} handler The name of the function or
     *  the function expression to run when the item is ready.
     * @returns {$w.Pagination} The pagination bar on which the event is now registered.
     * @eventtype Change
     * @eventhandler
     * @memberof $w.Pagination
     */

    onChange(cb) {
        const compType = members._type.get(this).compType;
        if (!validate({
            compType,
            functionName: 'onChange',
            dataToValidate: { onChange: cb },
            compName: this.id
        })) {
            return this;
        }

        const RMI = members._RMI.get(this);
        const contextId = members._contextId.get(this);
        const controllerId = members._controllerId.get(this);
        const compId = members._compId.get(this);
        RMI.registerEvent(contextId, compId, RMI.getEventTypes().CHANGE, controllerId, cb);
        return this;
    }

    /**
     * @member currentPage
     * @label property
     * @syntax
     * get currentPage(): number
     * set currentPage(value: number): void
     * @summary Sets or gets the current page.
     * @description
     *  Setting the `currentPage` property updates the visual indicator of the
     *  pagination bar to display the current page. The `currentPage` cannot be
     *  set to a value greater than [`totalPages`](#totalPages).
     *
     *  If the pagination bar is connected to a dataset, setting `currentPage`
     *  updates the current page of the dataset and updates any relevant
     *  elements connected to that dataset.
     *
     *  Getting the `currentPage` property returns the index of the current page.
     * @note
     *  Page numbers begin with 1.
     * @snippet [Pagination-currentPage_get.es6=Get a pagination bar's current page]
     * @snippet [Pagination-currentPage_set.es6=Set a pagination bar's current page]
     * @type {external:Number}
     * @memberof $w.Pagination
     * @see [totalPages](#totalPages)
     * @instance
     */
    get currentPage () {
        return Utils.getData(this, 'currentPage');
    }

    /*
     * @param {external:Number} currentPage
     * @ignore
     */
    set currentPage (currentPage) {
        const compType = members._type.get(this).compType;
        const acceptNil = true;
        const index = null;

        if(!validate({
            compType,
            functionName: 'currentPage',
            dataToValidate: {currentPage},
            compName: this.id
        }) || !validateRange({
            functionName: 'currentPage',
            propertyName: 'currentPage',
            value: currentPage,
            minValue: 1,
            maxValue: this.totalPages,
            itemIndex: index,
            acceptNil
        })) { return; }

        if (_.isNil(currentPage)){
            currentPage = 1;
        }

        Utils.setData(this, { currentPage });
    }

    /**
     * @member totalPages
     * @label property
     * @syntax
     * get totalPages(): number
     * set totalPages(value: number): void
     * @summary Sets or gets the total number of pages shown in a pagination bar.
     * @description
     *  Setting the `totalPages` property updates the number of pages shown in
     *  the pagination bar.
     *
     *  Setting `totalPages` to a value less than [`currentPage`](#currentPage)
     *  causes `currentPage` to be reset to the new `totalPages` value. For
     *  example, if `currentPage` is `8` and `totalPages` is set to `5`, `currentPage`
     *  is reset automatically to `5`.
     *
     *  If the pagination bar is connected to a dataset, it is recommended that
     *  you allow the dateset to control the pagination bar's `totalPages` value.
     *
     *  Getting the `totalPages` property returns the total number of pages available
     *  for selection in the pagination bar. If the pagination bar is
     *  connected to a dataset, the initial `totalPages` value is calculated based
     *  on the total number of items in the dataset and the dataset's page size.
     * @snippet [Pagination-totalPages_get.es6=Get the number of pages available in a pagination element]
     * @snippet [Pagination-totalPages_set.es6=Set the number of pages available in a pagination element]
     * @type {external:Number}
     * @memberof $w.Pagination
     * @see [currentPage](#currentPage)
     * @instance
     */
    get totalPages () {
        return Utils.getData(this, 'totalPages');
    }

    /*
     * @param {external:Number} total Pages
     * @ignore
     */
    set totalPages (totalPages) {
        const compType = members._type.get(this).compType;
        if (!validate({
            compType,
            functionName: 'totalPages',
            dataToValidate: {totalPages},
            compName: this.id
        })) {
            return;
        }

        const newData = { totalPages };

        if (Utils.getData(this, 'currentPage') > totalPages) {
            newData.currentPage = totalPages;
            reporter.reportSdkWarning(generateError.warning_value_changed({
                propertyName: 'totalPages',
                compName: this.id,
                newValue: totalPages,
                changedProperty: 'currentPage'
            }));
        }

        Utils.setData(this, newData);
    }

    toJSON() {
        const {id, type, global, rendered, currentPage, totalPages} = this;
        return {id, type, global, rendered, currentPage, totalPages};
    }
}

mixin(Pagination.prototype, DisabledMixin.prototype);
mixin(Pagination.prototype, HiddenCollapsedMixin.prototype);
mixin(Pagination.prototype, ClickableMixin.prototype);

export default Pagination;



// WEBPACK FOOTER //
// ./js/modules/ui/Pagination.es6